import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { opportunitiesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { 
  MapPin, TrendingUp, Calendar, Clock, FileText, 
  AlertTriangle, ChevronRight, Download, Lock,
  Building2, Users, DollarSign, Loader2, Shield
} from 'lucide-react';

const OpportunityDetailPage = () => {
  const { slug } = useParams();
  const { formatCurrency, formatNumber } = useI18n();
  const { isAuthenticated, user } = useAuth();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchOpportunity();
  }, [slug]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opportunities/${slug}/pdf`);
      if (!response.ok) throw new Error('Error descargando PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `silver_brick_${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const fetchOpportunity = async () => {
    setLoading(true);
    try {
      const response = await opportunitiesAPI.get(slug);
      const opp = response.data;
      // Map API response to expected format (API returns flat structure)
      setOpportunity({
        opportunity_id: opp.opportunity_id,
        title: opp.title || opp.title_es || 'Sin título',
        slug: opp.slug,
        project_code: opp.project_code,
        status: opp.status,
        project_type: opp.project_type,
        investment_structure: opp.investment_structure,
        summary: opp.summary || opp.summary_es || '',
        thesis: opp.thesis || opp.thesis_es || '',
        location_address: opp.location_address || '',
        location_city: opp.location_city || '',
        location_state: opp.location_state || '',
        location_country: opp.location_country || 'México',
        sponsor_name: opp.sponsor_name || 'Silver Brick Development',
        sponsor_info: opp.sponsor_info || '',
        capital_target: opp.capital_target || 0,
        capital_target_currency: opp.capital_target_currency || 'MXN',
        minimum_investment: opp.minimum_investment || 0,
        maximum_investment: opp.maximum_investment || opp.capital_target || 0,
        funding_progress: opp.funding_progress || 0,
        timeline_months: opp.timeline_months || 36,
        hold_period_months: opp.hold_period_months || 36,
        projected_irr_base: opp.projected_irr_base || 15,
        projected_irr_upside: opp.projected_irr_upside || (opp.projected_irr_base || 15) + 4,
        projected_irr_downside: opp.projected_irr_downside || (opp.projected_irr_base || 15) - 6,
        projected_multiple: opp.projected_moic_base || 1.5,
        distribution_frequency: 'quarterly',
        risk_level: opp.risk_level || 'medium',
        risk_factors: opp.risk_factors || [
          'Riesgo de construcción y retrasos potenciales',
          'Fluctuaciones en tasas de interés',
          'Riesgo de mercado inmobiliario',
        ],
        key_assumptions: opp.key_assumptions || [
          'Ocupación proyectada según estudio de mercado',
          'Costos de construcción actualizados',
          'Proyecciones de venta conservadoras',
        ],
        legal_structure: opp.legal_structure || 'Fideicomiso de inversión en bienes raíces',
        fees_summary: opp.fees_summary || '',
        use_of_funds: opp.use_of_funds || '',
        capital_stack: opp.capital_stack || { equity: 40, senior_debt: 60 },
        important_dates: opp.important_dates || {},
        media_gallery: opp.media_gallery || [],
        gallery_images: opp.gallery_images || [],
        image_hero_url: opp.image_hero_url || null,
        image_cover_url: opp.image_cover_url || null,
        image_card_url: opp.image_card_url || null,
        developer_name: opp.developer_name || opp.operator_name || opp.sponsor_name || 'Desarrollador por confirmar',
        is_featured: opp.is_featured || false,
      });
    } catch (error) {
      console.error('Error fetching opportunity:', error);
      // Use sample data for demo
      setOpportunity(getSampleOpportunity());
    } finally {
      setLoading(false);
    }
  };

  const getSampleOpportunity = () => ({
    opportunity_id: '1',
    title: 'Torre Reforma Elite',
    slug: 'torre-reforma-elite',
    project_code: 'TRE-2024-001',
    status: 'open',
    project_type: 'Desarrollo Mixto',
    investment_structure: 'SPV (Sociedad de Propósito Específico)',
    summary: 'Desarrollo de torre de uso mixto de 35 pisos en Paseo de la Reforma con oficinas premium clase A+ y espacios comerciales de alta gama.',
    thesis: 'El proyecto capitaliza la creciente demanda de espacios de oficina premium en el corredor Reforma, aprovechando la escasez de inventario nuevo de alta calidad y la recuperación del mercado post-pandemia. La ubicación estratégica y las amenidades de clase mundial posicionan al proyecto para capturar inquilinos institucionales con contratos a largo plazo.',
    location_address: 'Paseo de la Reforma 505',
    location_city: 'Ciudad de México',
    location_state: 'CDMX',
    location_country: 'México',
    sponsor_name: 'Desarrollo Premium S.A. de C.V.',
    sponsor_info: 'Desarrollador con más de 20 años de experiencia y 15 proyectos completados exitosamente en la CDMX.',
    capital_target: 250000000,
    capital_target_currency: 'MXN',
    minimum_investment: 500000,
    maximum_investment: 25000000,
    funding_progress: 65,
    timeline_months: 36,
    hold_period_months: 48,
    projected_irr_base: 18,
    projected_irr_upside: 22,
    projected_irr_downside: 12,
    risk_level: 'medium',
    risk_factors: [
      'Riesgo de construcción y retrasos potenciales',
      'Fluctuaciones en tasas de interés',
      'Riesgo de vacancia en fase de comercialización',
      'Variaciones en costos de materiales',
      'Riesgo regulatorio y de permisos',
    ],
    key_assumptions: [
      'Ocupación estabilizada del 92% en 18 meses post-entrega',
      'Rentas promedio de $35 USD/m²/mes en oficinas',
      'Cap rate de salida del 7.5%',
      'Inflación promedio del 4.5% anual',
    ],
    legal_structure: 'Fideicomiso de inversión en bienes raíces con estructura SPV para el proyecto específico.',
    fees_summary: 'Comisión de estructuración 2%, Comisión de administración 1.5% anual sobre capital comprometido, Carried interest 20% sobre rendimientos superiores al 12% TIR.',
    use_of_funds: 'Adquisición de terreno (25%), Construcción (55%), Soft costs y permisos (10%), Reserva de contingencia (5%), Gastos de comercialización (5%).',
    capital_stack: {
      senior_debt: 50,
      mezzanine: 15,
      equity: 35,
    },
    important_dates: {
      funding_deadline: '2024-06-30',
      construction_start: '2024-09-01',
      expected_completion: '2027-06-30',
    },
    media_gallery: [
      'https://images.unsplash.com/photo-1579283987703-5ae978f95ace?q=80&w=2574&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1579281959510-26b99ae23ae3?q=80&w=2574&auto=format&fit=crop',
    ],
    is_featured: true,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="min-h-screen bg-background pt-20">
        <div className="container-app py-24 text-center">
          <h1 className="heading-2 mb-4">Oportunidad no encontrada</h1>
          <Link to="/opportunities">
            <Button variant="outline">Ver todas las oportunidades</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isVerified = user?.verification_status === 'approved';

  return (
    <div className="min-h-screen bg-background pt-20">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${opportunity.image_hero_url || opportunity.image_cover_url || opportunity.media_gallery?.[0] || 'https://images.unsplash.com/photo-1579283987703-5ae978f95ace?q=80&w=2574&auto=format&fit=crop'})`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/30" />
        </div>
        <div className="container-app relative h-full flex items-end pb-12">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <Badge 
                variant="outline" 
                className={opportunity.status === 'open' 
                  ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' 
                  : 'bg-amber-500/10 text-amber-500 border-amber-500/20'
                }
              >
                {opportunity.status === 'open' ? 'Abierto' : 'Soft Circle'}
              </Badge>
              <span className="text-sm text-muted-foreground">{opportunity.project_code}</span>
            </div>
            <h1 className="heading-1 mb-4">{opportunity.title}</h1>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4" strokeWidth={1.5} />
                {opportunity.location_city}, {opportunity.location_state}
              </span>
              <span className="flex items-center gap-2">
                <Building2 className="h-4 w-4" strokeWidth={1.5} />
                {opportunity.project_type}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-spacing">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Tabs defaultValue="summary" className="w-full">
                <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent p-0 mb-8">
                  <TabsTrigger value="summary" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
                    Resumen
                  </TabsTrigger>
                  <TabsTrigger value="financials" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
                    Financiero
                  </TabsTrigger>
                  <TabsTrigger value="documents" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
                    Documentos
                  </TabsTrigger>
                  <TabsTrigger value="risks" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
                    Riesgos
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="summary" className="mt-0">
                  <div className="space-y-8">
                    <div>
                      <h3 className="heading-3 mb-4">Descripción del Proyecto</h3>
                      <p className="body-text">{opportunity.summary}</p>
                    </div>
                    
                    {opportunity.thesis && (
                      <div>
                        <h3 className="heading-3 mb-4">Tesis de Inversión</h3>
                        <p className="body-text">{opportunity.thesis}</p>
                      </div>
                    )}

                    <div>
                      <h3 className="heading-3 mb-4">Estructura Legal</h3>
                      <p className="body-text">{opportunity.legal_structure}</p>
                    </div>

                    {opportunity.sponsor_name && (
                      <div className="card-dashboard">
                        <h3 className="text-lg font-medium mb-4">Sponsor / Operador</h3>
                        <p className="font-medium mb-2">{opportunity.sponsor_name}</p>
                        <p className="text-sm text-muted-foreground">{opportunity.sponsor_info}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="financials" className="mt-0">
                  <div className="space-y-8">
                    {/* Projected Returns */}
                    <div className="card-dashboard">
                      <h3 className="text-lg font-medium mb-6">Escenarios de Retorno (TIR)</h3>
                      <div className="grid grid-cols-3 gap-6">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Conservador</p>
                          <p className="text-3xl font-light">{opportunity.projected_irr_downside}%</p>
                        </div>
                        <div className="text-center border-x border-border">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Base</p>
                          <p className="text-3xl font-light text-primary">{opportunity.projected_irr_base}%</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Optimista</p>
                          <p className="text-3xl font-light">{opportunity.projected_irr_upside}%</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-6 text-center">
                        * Las proyecciones son estimaciones y no garantizan resultados futuros
                      </p>
                    </div>

                    {/* Capital Stack */}
                    {opportunity.capital_stack && (
                      <div>
                        <h3 className="heading-3 mb-4">Estructura de Capital</h3>
                        <div className="space-y-4">
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span>Deuda Senior</span>
                              <span>{opportunity.capital_stack.senior_debt}%</span>
                            </div>
                            <Progress value={opportunity.capital_stack.senior_debt} className="h-2" />
                          </div>
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span>Mezzanine</span>
                              <span>{opportunity.capital_stack.mezzanine}%</span>
                            </div>
                            <Progress value={opportunity.capital_stack.mezzanine} className="h-2" />
                          </div>
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span>Capital (Equity)</span>
                              <span>{opportunity.capital_stack.equity}%</span>
                            </div>
                            <Progress value={opportunity.capital_stack.equity} className="h-2" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Use of Funds */}
                    <div>
                      <h3 className="heading-3 mb-4">Uso de Fondos</h3>
                      <p className="body-text">{opportunity.use_of_funds}</p>
                    </div>

                    {/* Fees */}
                    <div>
                      <h3 className="heading-3 mb-4">Comisiones y Costos</h3>
                      <p className="body-text">{opportunity.fees_summary}</p>
                    </div>

                    {/* Key Assumptions */}
                    {opportunity.key_assumptions && (
                      <div>
                        <h3 className="heading-3 mb-4">Supuestos Clave</h3>
                        <ul className="space-y-2">
                          {opportunity.key_assumptions.map((assumption, index) => (
                            <li key={index} className="flex items-start gap-3 text-sm text-muted-foreground">
                              <ChevronRight className="h-4 w-4 mt-0.5 text-primary" strokeWidth={1.5} />
                              {assumption}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="documents" className="mt-0">
                  <div className="space-y-4">
                    {isAuthenticated && isVerified ? (
                      <>
                        <div className="card-dashboard flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <FileText className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                            <div>
                              <p className="font-medium">Teaser Ejecutivo</p>
                              <p className="text-xs text-muted-foreground">PDF - 2.4 MB</p>
                            </div>
                          </div>
                          <Button variant="outline" size="sm" className="gap-2">
                            <Download className="h-4 w-4" strokeWidth={1.5} />
                            Descargar
                          </Button>
                        </div>
                        <div className="card-dashboard flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <FileText className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                            <div>
                              <p className="font-medium">Memorándum de Inversión</p>
                              <p className="text-xs text-muted-foreground">PDF - 8.1 MB</p>
                            </div>
                          </div>
                          <Button variant="outline" size="sm" className="gap-2">
                            <Download className="h-4 w-4" strokeWidth={1.5} />
                            Descargar
                          </Button>
                        </div>
                        <div className="card-dashboard flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <FileText className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                            <div>
                              <p className="font-medium">Modelo Financiero</p>
                              <p className="text-xs text-muted-foreground">XLSX - 1.2 MB</p>
                            </div>
                          </div>
                          <Button variant="outline" size="sm" className="gap-2">
                            <Download className="h-4 w-4" strokeWidth={1.5} />
                            Descargar
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="card-dashboard text-center py-12">
                        <Lock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
                        <h3 className="text-lg font-medium mb-2">Documentos Restringidos</h3>
                        <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                          {isAuthenticated 
                            ? 'Su cuenta debe estar verificada para acceder a los documentos del proyecto.'
                            : 'Inicie sesión y complete su verificación para acceder a los documentos del proyecto.'
                          }
                        </p>
                        <Link to={isAuthenticated ? '/app/profile' : '/login'}>
                          <Button className="btn-primary">
                            {isAuthenticated ? 'Completar Verificación' : 'Iniciar Sesión'}
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="risks" className="mt-0">
                  <div className="space-y-6">
                    <div className="card-dashboard border-amber-500/20 bg-amber-500/5">
                      <div className="flex items-start gap-4">
                        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div>
                          <h3 className="font-medium text-primary mb-2">Nuestro Compromiso con Usted</h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            Como en todo negocio, existen variables a considerar. Sin embargo, al tratarse de productos de inversión inmobiliaria de un fondo de alto nivel institucional, el riesgo se mitiga al máximo posible.
                          </p>
                          <p className="text-sm text-muted-foreground mb-3">
                            Contamos con contratos sólidos y las herramientas financieras y jurídicas más adecuadas para brindarle la tranquilidad que merece. Nuestro objetivo es construir relaciones de largo plazo con nuestros inversionistas, basadas en confianza y resultados.
                          </p>
                          <p className="text-sm text-primary font-medium">
                            Silver Brick Development: Su patrimonio en las mejores manos.
                          </p>
                        </div>
                      </div>
                    </div>

                    {opportunity.risk_factors && (
                      <div>
                        <h3 className="heading-3 mb-4">Factores de Riesgo</h3>
                        <ul className="space-y-3">
                          {opportunity.risk_factors.map((risk, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <span className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium flex-shrink-0">
                                {index + 1}
                              </span>
                              <span className="text-sm text-muted-foreground">{risk}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Investment Card */}
              <div className="card-dashboard sticky top-28">
                <div className="space-y-6">
                  <div>
                    <p className="caption mb-2">Capital Objetivo</p>
                    <p className="text-3xl font-light">{formatCurrency(opportunity.capital_target)}</p>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Avance de Fondeo</span>
                      <span className="font-medium">{opportunity.funding_progress}%</span>
                    </div>
                    <Progress value={opportunity.funding_progress} className="h-2" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Inversión Mínima</p>
                      <p className="font-medium">{formatCurrency(opportunity.minimum_investment)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Inversión Máxima</p>
                      <p className="font-medium">{opportunity.maximum_investment ? formatCurrency(opportunity.maximum_investment) : 'Sin límite'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Plazo Construcción</p>
                      <p className="font-medium">{opportunity.timeline_months} meses</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Período de Tenencia</p>
                      <p className="font-medium">{opportunity.hold_period_months} meses</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-muted-foreground">TIR Objetivo</p>
                      <p className="text-xl font-medium text-primary flex items-center gap-1">
                        <TrendingUp className="h-4 w-4" strokeWidth={1.5} />
                        {opportunity.projected_irr_base}-{opportunity.projected_irr_upside}%
                      </p>
                    </div>
                  </div>

                  {isAuthenticated && isVerified ? (
                    <Link to={`/app/opportunities/${opportunity.slug}/invest`}>
                      <Button className="w-full btn-primary" data-testid="invest-btn">
                        Invertir en Este Proyecto
                      </Button>
                    </Link>
                  ) : (
                    <Link to={isAuthenticated ? '/app/profile' : '/register'}>
                      <Button className="w-full btn-primary">
                        {isAuthenticated ? 'Completar Verificación' : 'Registrarse para Invertir'}
                      </Button>
                    </Link>
                  )}

                  <Button 
                    variant="outline" 
                    className="w-full gap-2" 
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf}
                    data-testid="download-pdf-btn"
                  >
                    {downloadingPdf ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                        Generando PDF...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" strokeWidth={1.5} />
                        Descargar Información Completa
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    Al continuar, acepta nuestros términos y reconoce los riesgos de inversión.
                  </p>
                </div>
              </div>

              {/* Important Dates */}
              {opportunity.important_dates && (
                <div className="card-dashboard">
                  <h3 className="font-medium mb-4 flex items-center gap-2">
                    <Calendar className="h-4 w-4" strokeWidth={1.5} />
                    Fechas Importantes
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Cierre de Fondeo</span>
                      <span>{opportunity.important_dates.funding_deadline}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Inicio Construcción</span>
                      <span>{opportunity.important_dates.construction_start}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Entrega Esperada</span>
                      <span>{opportunity.important_dates.expected_completion}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OpportunityDetailPage;
